import { useEffect, useState } from 'react'
import activityApi from '@/apis/activity'
import getHostNameObj from '@/hooks/get-host-name-obj'
import { download, isIOS, isInWeChat } from '@bihu/common-js'
import { useSelector } from 'react-redux'
import { RootState } from '@/store'
import { resolveDownloadPresentation, DownloadPresentation } from '@/pages/application-flow-skins/theme/download-presentation'
import { getApplicationFlowTheme } from '@/pages/application-flow-skins/theme/application-flow-theme'
import { ApplicationFlowTheme } from '@/pages/application-flow-skins/types'

export interface UseV2PageDownloadBusinessBridgeResult {

  /** 品牌名称文案。 */
  brandName: string

  /** 确定渲染的下载皮肤主题（优先从渠道解析，无渠道参数时回退 new-purple）。 */
  theme: ApplicationFlowTheme | null

  /** 是否正在向后端查询渠道详情，用于阻止闪现。 */
  isLoadingChannel: boolean

  /** 旧版本接口准备好可用下载链接（已按 iOS / Android 环境分别判定）。 */
  isDownloadReady: boolean

  /** 是否按聚好借品牌展示专属标识。 */
  isJhjBrand: boolean

  /** 微信浏览器内“在浏览器打开”旧提示的展示状态。 */
  showWechatTip: boolean

  /** 关闭微信提示的回调。 */
  closeWechatTip: () => void

  /** 用户在下载皮肤中触发的真实下载与上报动作。 */
  handleDownload: () => void
}

/**
 * useV2PageDownloadBusinessBridge — 通用下载页旧业务 Bridge Hook
 * 在旧页面目录内管理渠道详情、版本请求、微信判断与下载触发，不改变任何既有规则。
 * @returns {UseV2PageDownloadBusinessBridgeResult} 业务桥接数据与动作
 */
export const useV2PageDownloadBusinessBridge = (): UseV2PageDownloadBusinessBridgeResult => {
  const hostNameObj = getHostNameObj()
  const { adChannelCode } = useSelector((state: RootState) => state.channel)
  const [showWechatTip, setShowWechatTip] = useState<boolean>(isInWeChat)
  const [androidUrl, setAndroidUrl] = useState('')
  const [iosUrl, setIosUrl] = useState('')
  const [channelDetail, setChannelDetail] = useState<unknown>(undefined)

  useEffect(() => {
    if (!adChannelCode) {
      return
    }

    activityApi.getChannelCodeDetail(adChannelCode)
      .then(detail => setChannelDetail(detail ?? null))
      .catch(() => setChannelDetail(null))
  }, [adChannelCode])

  useEffect(() => {
    async function getAppDownloadUrl() {
      try {
        const res = await activityApi.getNewestVersion({
          appType: hostNameObj.appType,
        })
        setAndroidUrl(res.androidPackageLocation)
        setIosUrl(res.iosPackageLocation)
      } catch {
        // empty
      }
    }
    getAppDownloadUrl()
  }, [hostNameObj.appType])

  const handleDownload = () => {
    if (isIOS) {
      if (iosUrl) {
        window.location.href = iosUrl
      }
      return
    }

    if (isInWeChat) {
      setShowWechatTip(true)
      return
    }

    if (androidUrl) {
      download(androidUrl, hostNameObj.appName, 'apk')
    }
  }

  const downloadPresentation = resolveDownloadPresentation(channelDetail)
  const isLoadingChannel = Boolean(adChannelCode) && channelDetail === undefined

  // 只有在明确知道无渠道参数或请求完成时，才决定是否回退默认主题
  const theme = downloadPresentation?.theme || (!adChannelCode ? getApplicationFlowTheme(2) : null)
  const brandName = downloadPresentation?.brandName || (hostNameObj.appName === '聚好借' ? '聚好借' : '聚融易')
  const isJhjBrand = downloadPresentation?.isJhjBrand ?? (hostNameObj.appName === '聚好借')

  // 区分设备类型精确判定下载链接就绪状态
  const isDownloadReady = Boolean(isIOS ? iosUrl : androidUrl)

  return {
    brandName,
    theme,
    isLoadingChannel,
    isDownloadReady,
    isJhjBrand,
    showWechatTip,
    closeWechatTip: () => setShowWechatTip(false),
    handleDownload,
  }
}

export default useV2PageDownloadBusinessBridge
